import React, { useState, useMemo, useRef } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  InputAdornment,
  TextField,
  Tooltip,
  Switch,
  Popover,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Checkbox,
  Snackbar,
  Alert
} from '@mui/material'
import AddIcon from '@mui/icons-material/AddRounded'
import EditIcon from '@mui/icons-material/EditOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import SearchIcon from '@mui/icons-material/SearchRounded'
import FilterListIcon from '@mui/icons-material/FilterListRounded'
import ClearIcon from '@mui/icons-material/ClearRounded'
import CancelIcon from '@mui/icons-material/CancelRounded'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlineRounded'
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenuOutlined'
import { useQuery, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { GET_RESTAURANT_ITEMS, GET_ADVANCED_MODIFIER_GROUPS, GET_ADVANCED_OPTION_GROUPS } from '../apollo/advancedMenuQueries'
import { DELETE_ADVANCED_MENU_ITEM, UPDATE_ADVANCED_MENU_ITEM, MARK_AS_EIGHTY_SIX, UNMARK_EIGHTY_SIX, BULK_ATTACH_MODIFIER_GROUP_TO_ITEMS, ATTACH_MODIFIER_GROUP_TO_ITEM, DETACH_MODIFIER_GROUP_FROM_ITEM, ATTACH_OPTION_GROUP_TO_ITEM, DETACH_OPTION_GROUP_FROM_ITEM, GENERATE_ALL_ITEMS_AI_CONTENT } from '../apollo/advancedMenuMutations'
import { GET_MENU_LANGUAGES } from '../apollo/advancedMenuMutations'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesomeOutlined'
import AutoAwesomeFilledIcon from '@mui/icons-material/AutoAwesome'
import LanguageIcon from '@mui/icons-material/LanguageOutlined'
import { Menu as MuiMenu } from '@mui/material'
import ItemForm from '../components/AdvancedMenu/ItemForm'
import MenuLanguagesDialog from '../components/AdvancedMenu/MenuLanguagesDialog'
import useBulkSelection, { bulkDelete } from '../components/Common/useBulkSelection'
import { mw, stickyToolbarSx, tableCardStickySx, headCellSx, filterBarSx, searchFieldSx, chipSx, pillBtnSx, pillBtnPrimarySx, pillBtnDangerSx, aiBtnSx, dialogPaperSx, menuPaperSx, inlinePriceSx, sectionLabelSx, iconBtnSx, iconBtnDangerSx, addGroupBtnSx, displaySx, eyebrowSx } from './menuWorkspaceStyles'

// Filter selects: a small uppercase label above the field instead of the
// floating outline label (the notch is closed so the border runs unbroken).
const filterSelectSx = {
  flex: '0 1 160px',
  minWidth: 132,
  '& .MuiInputLabel-root': {
    position: 'static',
    transform: 'none',
    mb: 0.75,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: mw.color.textMuted,
    '&.Mui-focused': { color: mw.color.accent },
  },
  '& .MuiOutlinedInput-notchedOutline legend': { maxWidth: '0.01px', padding: 0 },
  '& .MuiOutlinedInput-root': { height: 44, fontSize: 14.5 },
}

// Sentinel value for the "items with no category" choice in the Category
// filter. Bracketed so it can never collide with a real category name.
const UNCATEGORIZED = '__uncategorized__'

const ItemsTab = () => {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  // True while the open item editor has unsaved changes (reported by ItemForm).
  // Kept in a ref, not state: the form's delayed post-save close runs from a
  // closure captured before the save, and state reads there are stale — the
  // prompt would fire even though the item saved. A ref always reads current.
  const isDirtyRef = React.useRef(false)
  const handleDirtyChange = React.useCallback((d) => { isDirtyRef.current = d }, [])
  // Floating "saved" toast shown after the editor closes — visible regardless
  // of where the user had scrolled inside the form.
  const [toast, setToast] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [eightySixTarget, setEightySixTarget] = useState(null) // item pending 86

  // Filters apply instantly as they change (no Apply button).
  const [appliedFilters, setAppliedFilters] = useState({
    category: 'all',
    ingredients: 'all',
    modifiers: 'all',
    status: 'all'
  })
  const setFilter = (key, value) => setAppliedFilters(prev => ({ ...prev, [key]: value }))

  const restaurantId = localStorage.getItem('restaurantId')

  const { data, loading: queryLoading, refetch: refetchItems } = useQuery(GET_RESTAURANT_ITEMS, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'network-only'
  })

  // The restaurant's chosen menu languages, so the item editor can label its
  // Alternate Name fields with the actual language instead of a generic hint.
  const [languagesOpen, setLanguagesOpen] = useState(false)
  const { data: langData, refetch: refetchLanguages } = useQuery(GET_MENU_LANGUAGES, {
    variables: { id: String(restaurantId) },
    skip: !restaurantId
  })
  const menuLanguages = {
    language1: langData?.restaurant?.menuLanguage2 || null,
    language2: langData?.restaurant?.menuLanguage3 || null
  }

  const [deleteItem] = useMutation(DELETE_ADVANCED_MENU_ITEM, {
    refetchQueries: [{ query: GET_RESTAURANT_ITEMS, variables: { restaurantId } }],
    onError: (err) => console.error('Delete error:', err)
  })

  const [markAs86] = useMutation(MARK_AS_EIGHTY_SIX, {
    refetchQueries: [{ query: GET_RESTAURANT_ITEMS, variables: { restaurantId } }],
    onError: (err) => console.error('Mark as 86 error:', err)
  })

  const [unmark86] = useMutation(UNMARK_EIGHTY_SIX, {
    refetchQueries: [{ query: GET_RESTAURANT_ITEMS, variables: { restaurantId } }],
    onError: (err) => console.error('Unmark 86 error:', err)
  })

  // Bulk "attach modifier group to selected items"
  const { data: groupsData } = useQuery(GET_ADVANCED_MODIFIER_GROUPS, {
    variables: { restaurantId }, skip: !restaurantId
  })
  const modifierGroups = (groupsData?.advancedModifierGroups || []).slice().sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
  // Option groups — attachable from the same per-row "+" picker; checked-set
  // keys are prefixed ("mg:<id>" / "og:<id>") so both kinds share one state.
  const { data: optGroupsData } = useQuery(GET_ADVANCED_OPTION_GROUPS, {
    variables: { restaurantId }, skip: !restaurantId
  })
  const optionGroupsLib = (optGroupsData?.advancedOptionGroups || []).slice().sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
  const [attachGroupAnchor, setAttachGroupAnchor] = useState(null)
  const [bulkGroupChecked, setBulkGroupChecked] = useState(new Set())
  const [bulkGroupBusy, setBulkGroupBusy] = useState(false)
  const [bulkAttachGroup] = useMutation(BULK_ATTACH_MODIFIER_GROUP_TO_ITEMS, {
    refetchQueries: [{ query: GET_RESTAURANT_ITEMS, variables: { restaurantId } }],
    onError: (err) => alert(err.message)
  })

  // Per-item modifier-group management in the Modifier Groups column:
  // ✕ on a chip detaches, "+" opens a multi-select picker to attach.
  const itemsRefetch = { query: GET_RESTAURANT_ITEMS, variables: { restaurantId } }
  const [attachToItem, { loading: attachingItem }] = useMutation(ATTACH_MODIFIER_GROUP_TO_ITEM, {
    refetchQueries: [itemsRefetch], onError: (err) => alert(err.message)
  })
  const [detachFromItem, { loading: detachingItem }] = useMutation(DETACH_MODIFIER_GROUP_FROM_ITEM, {
    refetchQueries: [itemsRefetch], onError: (err) => alert(err.message)
  })
  const [attachOptToItem, { loading: attachingOptItem }] = useMutation(ATTACH_OPTION_GROUP_TO_ITEM, {
    refetchQueries: [itemsRefetch], onError: (err) => alert(err.message)
  })
  const [detachOptFromItem, { loading: detachingOptItem }] = useMutation(DETACH_OPTION_GROUP_FROM_ITEM, {
    refetchQueries: [itemsRefetch], onError: (err) => alert(err.message)
  })
  const itemGroupsBusy = attachingItem || detachingItem || attachingOptItem || detachingOptItem

  // Batch-generate AI image/description for all items missing them. Background
  // job — confirms cost, then tells the owner the count + ETA.
  const [generateAllAI, { loading: generatingAllAI }] = useMutation(GENERATE_ALL_ITEMS_AI_CONTENT, {
    onError: (err) => alert(err.message)
  })
  const handleGenerateAllAI = async () => {
    if (!window.confirm(t('Generate AI images and descriptions for all items that are missing them? This uses AI credits (only items without content are processed).'))) return
    const { data: res } = await generateAllAI({ variables: { restaurantId } })
    const r = res?.generateAllItemsAIContent
    if (!r) return
    if (r.queued === 0) {
      window.alert(t('All items already have an image and description. Nothing to generate.'))
    } else {
      window.alert(t('Started! Generating content for {{n}} item(s) in the background — about {{m}} minute(s). They will appear as each finishes; refresh to see progress.', { n: r.queued, m: r.estimatedMinutes }))
    }
  }

  const [itemGroupAnchor, setItemGroupAnchor] = useState(null)
  const [itemGroupTarget, setItemGroupTarget] = useState(null)
  const [itemGroupChecked, setItemGroupChecked] = useState(new Set())
  const [itemGroupBusy, setItemGroupBusy] = useState(false)

  const openItemGroupMenu = (e, item) => {
    e.stopPropagation()
    setItemGroupTarget(item)
    setItemGroupChecked(new Set())
    setItemGroupAnchor(e.currentTarget)
  }
  const closeItemGroupMenu = () => {
    setItemGroupAnchor(null)
    setItemGroupTarget(null)
    setItemGroupChecked(new Set())
  }
  const toggleItemGroupChecked = (groupId) => {
    setItemGroupChecked((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })
  }
  const applyAttachGroupsToItem = async () => {
    if (!itemGroupTarget || itemGroupChecked.size === 0) { closeItemGroupMenu(); return }
    const itemId = itemGroupTarget._id
    const keys = [...itemGroupChecked]
    setItemGroupBusy(true)
    try {
      for (const key of keys) {
        if (key.startsWith('og:')) {
          await attachOptToItem({ variables: { itemId, optionGroupId: key.slice(3) } })
        } else {
          await attachToItem({ variables: { itemId, modifierGroupId: key.replace(/^mg:/, '') } })
        }
      }
      closeItemGroupMenu()
    } finally {
      setItemGroupBusy(false)
    }
  }
  const handleDetachGroupFromItem = (item, group) => {
    if (!window.confirm(t('Remove "{{g}}" from "{{i}}"? It stays on other items and in the library.', { g: group.name, i: item.name }))) return
    detachFromItem({ variables: { itemId: item._id, modifierGroupId: group._id } })
  }
  const handleDetachOptGroupFromItem = (item, group) => {
    if (!window.confirm(t('Remove "{{g}}" from "{{i}}"? It stays on other items and in the library.', { g: group.name, i: item.name }))) return
    detachOptFromItem({ variables: { itemId: item._id, optionGroupId: group._id } })
  }

  const items = data?.advancedRestaurantItems || []

  // Unique category list for the dropdown. Items can carry the same category in
  // different casing (AI imports use the menu's ALL-CAPS heading, manual edits
  // use Title Case) — collapse case-variants so "BOBA"/"Boba" show once. We keep
  // the most "Title Case"-looking spelling as the label.
  const categoryOptions = useMemo(() => {
    const titleCaseScore = (s) => {
      if (s === s.toUpperCase() && s !== s.toLowerCase()) return 0 // ALL CAPS
      if (s === s.toLowerCase() && s !== s.toUpperCase()) return 1 // all lower
      return 2 // mixed / Title Case
    }
    const byLower = new Map() // lowercased → best display spelling
    items.forEach(i => {
      if (i.categoryName) {
        i.categoryName.split(',').forEach(c => {
          const trimmed = c.trim()
          if (!trimmed) return
          const key = trimmed.toLowerCase()
          const current = byLower.get(key)
          if (!current || titleCaseScore(trimmed) > titleCaseScore(current)) {
            byLower.set(key, trimmed)
          }
        })
      }
    })
    return [...byLower.values()].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
  }, [items])

  // How many items sit in no category at all — surfaced on the dropdown option
  // so the owner can see at a glance whether anything needs filing.
  const uncategorizedCount = useMemo(
    () => items.filter(i => !i.categoryName || i.categoryName.split(',').every(c => !c.trim())).length,
    [items]
  )

  const isAnyFilterActive = appliedFilters.category !== 'all' ||
    appliedFilters.ingredients !== 'all' ||
    appliedFilters.modifiers !== 'all' ||
    appliedFilters.status !== 'all'

  const filteredItems = useMemo(() => {
    let result = items

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(i =>
        i.name.toLowerCase().includes(q) ||
        (i.categoryName && i.categoryName.toLowerCase().includes(q)) ||
        (i.description && i.description.toLowerCase().includes(q))
      )
    }

    // Category filter — match case-insensitively so the canonical dropdown label
    // ("Boba") still catches items tagged in another casing ("BOBA").
    if (appliedFilters.category === UNCATEGORIZED) {
      // Items that never got attached to a category (blank, whitespace, or a
      // list of empty segments like ", ,").
      result = result.filter(i =>
        !i.categoryName ||
        i.categoryName.split(',').every(c => !c.trim())
      )
    } else if (appliedFilters.category !== 'all') {
      const target = appliedFilters.category.toLowerCase()
      result = result.filter(i =>
        i.categoryName &&
        i.categoryName.split(',').map(c => c.trim().toLowerCase()).includes(target)
      )
    }

    // Ingredients filter — an ingredient is either a modifier or another item
    const ingredientCount = (i) =>
      (i.criticalIngredients?.length || 0) + (i.criticalIngredientItems?.length || 0)
    if (appliedFilters.ingredients === 'has') {
      result = result.filter(i => ingredientCount(i) > 0)
    } else if (appliedFilters.ingredients === 'none') {
      result = result.filter(i => ingredientCount(i) === 0)
    }

    // Modifiers filter
    if (appliedFilters.modifiers === 'has') {
      result = result.filter(i => i.modifierGroups && i.modifierGroups.length > 0)
    } else if (appliedFilters.modifiers === 'none') {
      result = result.filter(i => !i.modifierGroups || i.modifierGroups.length === 0)
    }

    // Status filter
    if (appliedFilters.status === 'on') {
      result = result.filter(i => !i.is86ed)
    } else if (appliedFilters.status === 'off') {
      result = result.filter(i => i.is86ed)
    }

    // Show items alphabetically by name (copy first — don't mutate the source).
    return result.slice().sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    )
  }, [items, searchQuery, appliedFilters])

  const handleClearFilters = () => {
    setAppliedFilters({ category: 'all', ingredients: 'all', modifiers: 'all', status: 'all' })
    setSearchQuery('')
  }

  const handleOpenCreate = () => {
    setEditingItem(null)
    isDirtyRef.current = false
    setDialogOpen(true)
  }

  const handleOpenEdit = (item) => {
    setEditingItem(item)
    isDirtyRef.current = false
    setDialogOpen(true)
  }

  // Close the editor. After a successful save the form clears its dirty flag,
  // so this only prompts when there are genuinely unsaved edits.
  const handleCloseDialog = () => {
    if (isDirtyRef.current && !window.confirm(t('You have unsaved changes. Close without saving? Your changes will be lost.'))) return
    setDialogOpen(false)
    setEditingItem(null)
    isDirtyRef.current = false
  }

  const handleDelete = (e, itemId) => {
    e.stopPropagation()
    if (window.confirm(t('Are you sure you want to permanently delete this item?'))) {
      deleteItem({ variables: { id: itemId } })
    }
  }

  const bulk = useBulkSelection(filteredItems.map(i => i._id))

  const handleBulkDelete = async () => {
    const ids = bulk.selectedIds
    if (ids.length === 0) return
    if (!window.confirm(t('Permanently delete {{n}} items?', { n: ids.length }))) return
    const { failed } = await bulkDelete(ids, (id) => deleteItem({ variables: { id } }))
    bulk.clear()
    if (failed > 0) alert(t('{{n}} items could not be deleted.', { n: failed }))
  }

  const closeBulkGroupMenu = () => {
    setAttachGroupAnchor(null)
    setBulkGroupChecked(new Set())
  }
  const toggleBulkGroupChecked = (key) => {
    setBulkGroupChecked((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  // Bulk picker: tick any mix of modifier and option groups, then Apply to
  // attach every ticked group to every selected item in one go.
  const applyBulkAttachGroups = async () => {
    const itemIds = bulk.selectedIds
    const keys = [...bulkGroupChecked]
    if (itemIds.length === 0 || keys.length === 0) { closeBulkGroupMenu(); return }
    setBulkGroupBusy(true)
    try {
      for (const key of keys) {
        if (key.startsWith('og:')) {
          const optionGroupId = key.slice(3)
          for (const itemId of itemIds) {
            await attachOptToItem({ variables: { itemId, optionGroupId } })
          }
        } else {
          await bulkAttachGroup({ variables: { itemIds, modifierGroupId: key.replace(/^mg:/, '') } })
        }
      }
      closeBulkGroupMenu()
      bulk.clear()
      alert(t('Attached {{g}} group(s) to {{n}} item(s).', { g: keys.length, n: itemIds.length }))
    } finally {
      setBulkGroupBusy(false)
    }
  }

  const handleToggle = (e, item) => {
    e.stopPropagation()
    if (item.is86ed) {
      unmark86({ variables: { targetType: 'ITEM', targetId: item._id } })
    } else {
      setEightySixTarget(item)
    }
  }

  const handleConfirm86 = (item, duration) => {
    let until = null
    if (duration === 'today') {
      const midnight = new Date()
      midnight.setHours(23, 59, 59, 0)
      until = midnight.toISOString()
    } else if (duration === '2h') {
      until = new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString()
    } else if (duration === '4h') {
      until = new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString()
    }
    // duration === 'manual' → until stays null (indefinite)
    markAs86({
      variables: {
        restaurantId,
        input: { targetType: 'ITEM', targetId: item._id, reason: 'Out of stock', until }
      }
    })
    setEightySixTarget(null)
  }

  // Quick inline price editing straight from the list. Single-price items get
  // an in-cell field (Enter/blur saves, Esc cancels); multi-size items get a
  // small popover with one field per size that saves the same way — leaving a
  // field commits it, there is no Save button to remember. The update mutation
  // requires name and pricingMode, so those are sent back unchanged alongside
  // the price.
  const [priceEdit, setPriceEdit] = useState(null) // { id, value }
  // `saved` is the last persisted map, so a blur that changed nothing (or left
  // a blank/invalid field) is a no-op instead of a write.
  const [variantEdit, setVariantEdit] = useState(null) // { id, name, anchor, values, saved }
  const [priceSaving, setPriceSaving] = useState(false)
  // Signature of the last price map written, so the blur + close pair that
  // "Done" produces does not send the same update twice.
  const lastPriceWriteRef = useRef('')
  const [updateItemPrice] = useMutation(UPDATE_ADVANCED_MENU_ITEM, {
    refetchQueries: [itemsRefetch],
    onError: (err) => alert(err.message)
  })

  const savePriceEdit = async () => {
    if (!priceEdit || priceSaving) return
    const item = items.find(i => i._id === priceEdit.id)
    const parsed = parseFloat(priceEdit.value)
    if (!item || Number.isNaN(parsed) || parsed < 0 || parsed === parseFloat(item.basePrice)) {
      setPriceEdit(null)
      return
    }
    setPriceSaving(true)
    try {
      await updateItemPrice({
        variables: { id: item._id, input: { name: item.name, pricingMode: 'SINGLE', basePrice: parsed } }
      })
      setPriceEdit(null)
    } finally {
      setPriceSaving(false)
    }
  }

  const openVariantEdit = (e, item) => {
    e.stopPropagation()
    let vp = item.variantPrices
    if (typeof vp === 'string') { try { vp = JSON.parse(vp) } catch (err) { vp = {} } }
    const values = Object.fromEntries(Object.entries(vp || {}).map(([k, v]) => [k, String(v)]))
    lastPriceWriteRef.current = ''
    setVariantEdit({
      id: item._id,
      name: item.name,
      anchor: e.currentTarget,
      values,
      saved: { ...values }
    })
  }

  // Autosave for the multi-size editor: called when a size field loses focus,
  // on Enter, and when the popover closes. A blank or negative entry keeps the
  // last saved price for that size rather than dropping the size from the map.
  const commitVariantEdit = async (edit = variantEdit) => {
    if (!edit) return
    const out = {}
    let changed = false
    for (const [size, raw] of Object.entries(edit.values)) {
      const parsed = parseFloat(raw)
      const valid = !Number.isNaN(parsed) && parsed >= 0
      const fallback = parseFloat(edit.saved?.[size])
      out[size] = valid ? parsed : (Number.isNaN(fallback) ? 0 : fallback)
      if (String(out[size]) !== String(parseFloat(edit.saved?.[size]))) changed = true
    }
    if (!changed || Object.keys(out).length === 0) return
    // Clicking "Done" blurs the field first, so the close path arrives right
    // behind that save carrying the identical map — skip it. Anything with a
    // NEW price still goes through, even mid-flight, so a fast tab-and-type
    // across sizes never loses an edit.
    const signature = `${edit.id}:${JSON.stringify(out)}`
    if (lastPriceWriteRef.current === signature) return
    lastPriceWriteRef.current = signature
    setPriceSaving(true)
    try {
      await updateItemPrice({
        variables: { id: edit.id, input: { name: edit.name, pricingMode: 'VARIANTS', variantPrices: out } }
      })
      const saved = Object.fromEntries(Object.entries(out).map(([k, v]) => [k, String(v)]))
      // Snap the fields back to what actually persisted, so a field left blank
      // shows the price it kept instead of staying empty.
      setVariantEdit(prev => (prev && prev.id === edit.id ? { ...prev, values: saved, saved } : prev))
      setToast(t('Prices saved'))
    } finally {
      setPriceSaving(false)
    }
  }

  const closeVariantEdit = async () => {
    const pending = variantEdit
    setVariantEdit(null)
    await commitVariantEdit(pending)
  }

  const formatPrice = (item) => {
    if (item.pricingMode === 'SINGLE' && item.basePrice != null) {
      return `$${item.basePrice}`
    } else if (item.pricingMode === 'VARIANTS' && item.variantPrices) {
      let vp = item.variantPrices
      if (typeof vp === 'string') {
        try { vp = JSON.parse(vp) } catch (e) { return '—' }
      }
      const prices = Object.values(vp).map(Number).filter(n => !isNaN(n))
      if (prices.length > 0) {
        const min = Math.min(...prices)
        const max = Math.max(...prices)
        return min === max ? `$${min}` : `$${min} - $${max}`
      }
    }
    return '—'
  }

  return (
    <Box>
      <Box sx={stickyToolbarSx}>
        <Typography variant="h4" fontWeight="bold" sx={{ color: mw.color.textPrimary }}>
          {t('Items')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {bulk.selectedIds.length > 0 && (
            <>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={(e) => setAttachGroupAnchor(e.currentTarget)}
                sx={pillBtnSx}
              >
                {t('Attach Modifier / Option Group')} ({bulk.selectedIds.length})
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
                sx={pillBtnDangerSx}
              >
                {t('Delete Selected')} ({bulk.selectedIds.length})
              </Button>
            </>
          )}
        </Box>
        <MuiMenu
          anchorEl={attachGroupAnchor}
          open={Boolean(attachGroupAnchor)}
          onClose={closeBulkGroupMenu}
          PaperProps={{ sx: { ...menuPaperSx, maxHeight: 420, width: 300 } }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" sx={{ color: mw.color.textMuted, fontWeight: 'bold' }}>
              {t('Add to {{n}} selected item(s)', { n: bulk.selectedIds.length })}
            </Typography>
          </Box>
          <Divider />
          {modifierGroups.length === 0 && optionGroupsLib.length === 0 ? (
            <MenuItem disabled>{t('No modifier groups yet')}</MenuItem>
          ) : (
            <>
              {modifierGroups.length > 0 && (
                <Box sx={{ px: 2, pt: 0.5 }}>
                  <Typography variant="caption" sx={sectionLabelSx.mg}>
                    {t('Modifier Groups')}
                  </Typography>
                </Box>
              )}
              {modifierGroups.map((g) => (
                <MenuItem key={`mg:${g._id}`} onClick={() => toggleBulkGroupChecked(`mg:${g._id}`)} dense>
                  <Checkbox
                    edge="start"
                    size="small"
                    checked={bulkGroupChecked.has(`mg:${g._id}`)}
                    tabIndex={-1}
                    disableRipple
                    onClick={(e) => { e.stopPropagation(); toggleBulkGroupChecked(`mg:${g._id}`) }}
                  />
                  <ListItemText primary={g.name} primaryTypographyProps={{ variant: 'body2' }} />
                </MenuItem>
              ))}
              {optionGroupsLib.length > 0 && (
                <Box sx={{ px: 2, pt: 0.5 }}>
                  <Typography variant="caption" sx={sectionLabelSx.og}>
                    {t('Option Groups')}
                  </Typography>
                </Box>
              )}
              {optionGroupsLib.map((g) => (
                <MenuItem key={`og:${g._id}`} onClick={() => toggleBulkGroupChecked(`og:${g._id}`)} dense>
                  <Checkbox
                    edge="start"
                    size="small"
                    checked={bulkGroupChecked.has(`og:${g._id}`)}
                    tabIndex={-1}
                    disableRipple
                    onClick={(e) => { e.stopPropagation(); toggleBulkGroupChecked(`og:${g._id}`) }}
                  />
                  <ListItemText primary={g.name} primaryTypographyProps={{ variant: 'body2' }} />
                </MenuItem>
              ))}
            </>
          )}
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" onClick={closeBulkGroupMenu} sx={{ textTransform: 'none' }}>
              {t('Cancel')}
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={bulkGroupBusy || bulkGroupChecked.size === 0}
              onClick={applyBulkAttachGroups}
              sx={{ textTransform: 'none', fontWeight: 'bold' }}
            >
              {bulkGroupBusy ? <CircularProgress size={18} /> : t('Add {{n}}', { n: bulkGroupChecked.size })}
            </Button>
          </Box>
        </MuiMenu>
      </Box>

      {/* Filter Bar */}
      {/* Grid: the header row spans both columns; the filters and the Clear
          Filters group share the second row on wide screens. */}
      <Paper variant="outlined" sx={{ ...filterBarSx, display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) auto' }, columnGap: 1.5, rowGap: 2.25 }}>
        <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: mw.radius.xs, bgcolor: mw.color.subtleBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FilterListIcon sx={{ color: mw.color.textSecondary, fontSize: 20 }} />
            </Box>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: mw.color.textPrimary, fontSize: 17, fontWeight: 700 }}>
              {t('Filter Items')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={generatingAllAI ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
              disabled={generatingAllAI}
              onClick={handleGenerateAllAI}
              sx={aiBtnSx}
            >
              {t('Generate AI Images & Descriptions')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<LanguageIcon />}
              onClick={() => setLanguagesOpen(true)}
              sx={aiBtnSx}
            >
              {t('Menu Languages')}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={pillBtnPrimarySx}
            >
              {t('New Item')}
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end', minWidth: 0 }}>
          {/* Search */}
          <TextField
            size="small"
            placeholder={t('Search by name, description...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: mw.color.textFaint, fontSize: 18 }} />
                </InputAdornment>
              )
            }}
            sx={{ ...searchFieldSx, flex: '1 1 220px', minWidth: 200, maxWidth: 380 }}
          />

          {/* Category */}
          <FormControl size="small" sx={filterSelectSx}>
            <InputLabel>{t('Category')}</InputLabel>
            <Select
              value={appliedFilters.category}
              label={t('Category')}
              onChange={(e) => setFilter('category', e.target.value)}
            >
              <MenuItem value="all">{t('All Categories')}</MenuItem>
              <MenuItem value={UNCATEGORIZED}>
                {t('No Category')}{uncategorizedCount > 0 ? ` (${uncategorizedCount})` : ''}
              </MenuItem>
              <Divider />
              {categoryOptions.map(cat => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Ingredients */}
          <FormControl size="small" sx={filterSelectSx}>
            <InputLabel>{t('Ingredients')}</InputLabel>
            <Select
              value={appliedFilters.ingredients}
              label={t('Ingredients')}
              onChange={(e) => setFilter('ingredients', e.target.value)}
            >
              <MenuItem value="all">{t('All Items')}</MenuItem>
              <MenuItem value="has">{t('Has Ingredients')}</MenuItem>
              <MenuItem value="none">{t('No Ingredients')}</MenuItem>
            </Select>
          </FormControl>

          {/* Modifiers */}
          <FormControl size="small" sx={filterSelectSx}>
            <InputLabel>{t('Modifiers')}</InputLabel>
            <Select
              value={appliedFilters.modifiers}
              label={t('Modifiers')}
              onChange={(e) => setFilter('modifiers', e.target.value)}
            >
              <MenuItem value="all">{t('All Items')}</MenuItem>
              <MenuItem value="has">{t('Has Modifiers')}</MenuItem>
              <MenuItem value="none">{t('No Modifiers')}</MenuItem>
            </Select>
          </FormControl>

          {/* Status */}
          <FormControl size="small" sx={{ ...filterSelectSx, flex: '0 1 130px', minWidth: 120 }}>
            <InputLabel>{t('Status')}</InputLabel>
            <Select
              value={appliedFilters.status}
              label={t('Status')}
              onChange={(e) => setFilter('status', e.target.value)}
            >
              <MenuItem value="all">{t('All Status')}</MenuItem>
              <MenuItem value="on">{t('ON')}</MenuItem>
              <MenuItem value="off">{t('OFF')}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', alignSelf: 'end', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
            disabled={!isAnyFilterActive && !searchQuery}
            sx={pillBtnSx}
          >
            {t('Clear Filters')}
          </Button>
          {isAnyFilterActive && (
            <Typography variant="body2" sx={{ color: mw.color.accent, alignSelf: 'center', fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {filteredItems.length} {t('of')} {items.length} {t('items')}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Seven columns: inner cells use 16px sides so the table fits its card. */}
      <TableContainer component={Paper} sx={{ ...tableCardStickySx, '& .MuiTableCell-root:not(.MuiTableCell-paddingCheckbox)': { px: 2 }, '& .MuiTableCell-root:last-of-type': { pr: 2.5 } }}>
        <Table>
          <TableHead>
            <TableRow sx={{ '& th': { position: 'sticky', top: 72, zIndex: 2, bgcolor: mw.color.headBg } }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={bulk.allSelected}
                  indeterminate={bulk.someSelected}
                  onChange={bulk.toggleAll}
                  onClick={(e) => e.stopPropagation()}
                />
              </TableCell>
              <TableCell sx={headCellSx}>{t('Name')}</TableCell>
              <TableCell sx={headCellSx}>{t('Category')}</TableCell>
              <TableCell sx={headCellSx}>{t('Price')}</TableCell>
              <TableCell sx={headCellSx}>{t('Modifier / Option Groups')}</TableCell>
              <TableCell sx={headCellSx} align="center">{t('Status')}</TableCell>
              <TableCell sx={headCellSx} align="right" width={100}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {queryLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6, color: mw.color.textMuted }}>
                  {items.length === 0
                    ? t('No items yet. Click "+ New Item" to create one.')
                    : t('No items match your filters.')}
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item) => (
                <TableRow
                  key={item._id}
                  hover
                  sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
                  onClick={() => handleOpenEdit(item)}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={bulk.isSelected(item._id)}
                      onChange={() => bulk.toggle(item._id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {(item.imageUrl || item.aiImageUrl) ? (
                        <Box sx={{ position: 'relative', flexShrink: 0 }}>
                          <Box
                            component="img"
                            src={item.imageUrl || item.aiImageUrl}
                            alt={item.name}
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: '14px',
                              objectFit: 'cover',
                              border: `1px solid ${item.imageUrl ? mw.color.border : mw.color.aiBorder}`,
                              display: 'block'
                            }}
                          />
                          {!item.imageUrl && (
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: -4,
                                right: -4,
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                bgcolor: mw.color.ai,
                                border: '2px solid #fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff'
                              }}
                            >
                              <AutoAwesomeFilledIcon sx={{ fontSize: 12, color: '#fff' }} />
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: '14px',
                            bgcolor: mw.color.subtleBg,
                            border: `1px solid ${mw.color.border}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            color: mw.color.textFaint
                          }}
                        >
                          <RestaurantMenuIcon sx={{ fontSize: 24, color: 'inherit' }} />
                        </Box>
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body1" fontWeight={500} sx={{ color: mw.color.textPrimary, fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
                          {item.name}
                        </Typography>
                        {item.description && (
                          <Typography variant="caption" sx={{ color: mw.color.textMuted, fontSize: 13, mt: 0.25 }} noWrap display="block" maxWidth={230}>
                            {item.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {item.categoryName ? (
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {item.categoryName.split(',').map(c => c.trim()).filter(Boolean).map((cat) => (
                          <Chip key={cat} label={cat} size="small" sx={chipSx.category} />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: mw.color.textFaint }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
                    {item.pricingMode !== 'VARIANTS' && priceEdit?.id === item._id ? (
                      <TextField
                        size="small"
                        autoFocus
                        type="number"
                        value={priceEdit.value}
                        disabled={priceSaving}
                        onChange={(e) => setPriceEdit({ id: item._id, value: e.target.value })}
                        onBlur={savePriceEdit}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); savePriceEdit() }
                          else if (e.key === 'Escape') setPriceEdit(null)
                        }}
                        inputProps={{ step: '0.01', min: 0, style: { width: 64 } }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          endAdornment: priceSaving ? <CircularProgress size={14} /> : null
                        }}
                      />
                    ) : (
                      <Tooltip title={item.pricingMode === 'VARIANTS' ? t('Click to edit size prices') : t('Click to edit price')}>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          onClick={(e) => {
                            if (item.pricingMode === 'VARIANTS') openVariantEdit(e, item)
                            else setPriceEdit({ id: item._id, value: item.basePrice != null ? String(item.basePrice) : '' })
                          }}
                          sx={inlinePriceSx}
                        >
                          {formatPrice(item)}
                        </Typography>
                      </Tooltip>
                    )}
                    {item.pricingMode === 'VARIANTS' && (
                      <Chip label={t('Multi-Size')} size="small" sx={{ ...chipSx.multiSize, mt: 0.75, height: 22, fontSize: 11.5, display: 'flex', width: 'fit-content' }} />
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                      {(item.modifierGroups || []).length === 0 && (item.optionGroups || []).length === 0 && (
                        <Typography variant="body2" sx={{ color: mw.color.textFaint }}>—</Typography>
                      )}
                      {(item.modifierGroups || []).map((mg) => (
                        <Chip
                          key={mg._id}
                          label={mg.name}
                          size="small"
                          onDelete={itemGroupsBusy ? undefined : () => handleDetachGroupFromItem(item, mg)}
                          deleteIcon={
                            <Tooltip title={t('Remove from item')}>
                              <CancelIcon />
                            </Tooltip>
                          }
                          sx={{ ...chipSx.modifier, ...chipSx.deleteIcon }}
                        />
                      ))}
                      {(item.optionGroups || []).map((og) => (
                        <Chip
                          key={og._id}
                          label={og.name}
                          size="small"
                          onDelete={itemGroupsBusy ? undefined : () => handleDetachOptGroupFromItem(item, og)}
                          deleteIcon={
                            <Tooltip title={t('Remove from item')}>
                              <CancelIcon />
                            </Tooltip>
                          }
                          sx={{ ...chipSx.option, ...chipSx.deleteIcon }}
                        />
                      ))}
                      <Tooltip title={t('Add modifier or option group')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={(modifierGroups.length === 0 && optionGroupsLib.length === 0) || itemGroupsBusy}
                            onClick={(e) => openItemGroupMenu(e, item)}
                            sx={addGroupBtnSx}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', columnGap: 0.25, rowGap: 0.5, minWidth: 92 }}>
                      <Switch
                        checked={!item.is86ed}
                        onChange={(e) => handleToggle(e, item)}
                        size="small"
                        color="success"
                      />
                      <Typography variant="caption" sx={{ color: item.is86ed ? mw.color.off : mw.color.onText, fontWeight: 700, fontSize: 12, letterSpacing: '0.04em', minWidth: 26 }}>
                        {item.is86ed ? t('OFF') : t('ON')}
                      </Typography>
                      {item.is86ed && item.eightysixReason && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: mw.color.amber,
                            flexBasis: '100%',
                            whiteSpace: 'nowrap',
                            fontSize: 11,
                            fontWeight: 600,
                            maxWidth: 140,
                            textAlign: 'center',
                            lineHeight: 1.3,
                            bgcolor: mw.color.amberSoft,
                            border: `1px solid ${mw.color.amberBorder}`,
                            borderRadius: mw.radius.badge,
                            px: 0.75,
                            py: 0.25,
                          }}
                        >
                          ⚠ {item.eightysixReason}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap', pl: 1 }}>
                    <Tooltip title={t('Edit')}>
                      <IconButton size="small" onClick={() => handleOpenEdit(item)} sx={iconBtnSx}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('Delete')}>
                      <IconButton size="small" color="error" onClick={(e) => handleDelete(e, item._id)} sx={iconBtnDangerSx}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Item Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { ...dialogPaperSx, maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ color: mw.color.textPrimary, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, px: 3.5, pt: 3, pb: 1.5 }}>
          {/* With an item name: small eyebrow + the name. Without: the title itself. */}
          <Box component="span" sx={{ display: 'block', minWidth: 0 }}>
            <Box component="span" sx={{ ...(editingItem?.name ? eyebrowSx : displaySx(26)), display: 'block' }}>{editingItem ? t('Edit Item') : t('New Item')}</Box>
            {editingItem?.name && (
              <Box component="span" sx={{ ...displaySx(26), display: 'block', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {editingItem.name}
              </Box>
            )}
          </Box>
          <Button onClick={handleCloseDialog} size="small" sx={{ ...pillBtnSx, height: 40 }}>{t('Close')}</Button>
        </DialogTitle>
        <DialogContent sx={{ px: 3.5 }}>
          <ItemForm
            item={editingItem}
            defaultCategoryName={!editingItem && appliedFilters.category !== 'all' && appliedFilters.category !== UNCATEGORIZED ? appliedFilters.category : undefined}
            onSuccess={(msg) => {
              handleCloseDialog()
              if (typeof msg === 'string' && msg) setToast(msg)
            }}
            onCancel={handleCloseDialog}
            onDirtyChange={handleDirtyChange}
            menuLanguages={menuLanguages}
          />
        </DialogContent>
      </Dialog>

      {/* Second/third menu language + AI translation of every name */}
      <MenuLanguagesDialog
        open={languagesOpen}
        onClose={() => setLanguagesOpen(false)}
        restaurantId={restaurantId}
        onGenerated={() => {
          refetchLanguages()
          // Translations land over the next few seconds; pull a fresh list once
          // so the first batch shows up without the owner reloading the page.
          setTimeout(() => refetchItems(), 8000)
        }}
      />

      {/* 86 Duration Picker Dialog */}
      <Dialog open={!!eightySixTarget} onClose={() => setEightySixTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ fontWeight: 'bold', color: mw.color.textPrimary, pb: 1 }}>
          Turn Off &quot;{eightySixTarget?.name}&quot;
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            How long should this item be turned off?
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {[
              { key: 'today', label: '🌙 Rest of today', sub: 'Auto turns back ON at midnight' },
              { key: '2h', label: '⏱ For 2 hours', sub: 'Auto turns back ON in 2 hours' },
              { key: '4h', label: '⏱ For 4 hours', sub: 'Auto turns back ON in 4 hours' },
              { key: 'manual', label: '🔒 Until I turn it back on', sub: 'Stays OFF until manually turned ON' },
            ].map(opt => (
              <Button
                key={opt.key}
                variant="outlined"
                onClick={() => handleConfirm86(eightySixTarget, opt.key)}
                sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1.2, px: 2, borderRadius: mw.radius.sm, borderColor: mw.color.border, transition: 'all .15s ease', '&:hover': { borderColor: mw.color.danger, bgcolor: mw.color.dangerSoft } }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>{opt.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{opt.sub}</Typography>
                </Box>
              </Button>
            ))}
          </Box>
        </DialogContent>
        <Box sx={{ px: 3, pb: 2 }}>
          <Button fullWidth onClick={() => setEightySixTarget(null)} sx={{ color: mw.color.textMuted, textTransform: 'none' }}>Cancel</Button>
        </Box>
      </Dialog>

      {/* Per-item "+" picker: tick one or more groups not already on the item,
          then Apply to attach them all at once. */}
      <MuiMenu
        anchorEl={itemGroupAnchor}
        open={Boolean(itemGroupAnchor)}
        onClose={closeItemGroupMenu}
        PaperProps={{ sx: { ...menuPaperSx, maxHeight: 420, width: 300 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" sx={{ color: mw.color.textMuted, fontWeight: 'bold' }}>
            {itemGroupTarget ? t('Add to "{{i}}"', { i: itemGroupTarget.name }) : t('Add modifier group')}
          </Typography>
        </Box>
        <Divider />
        {(() => {
          if (!itemGroupTarget) return null
          const onItemMg = new Set((itemGroupTarget.modifierGroups || []).map(g => g._id))
          const onItemOg = new Set((itemGroupTarget.optionGroups || []).map(g => g._id))
          const availableMg = modifierGroups.filter(g => !onItemMg.has(g._id))
          const availableOg = optionGroupsLib.filter(g => !onItemOg.has(g._id))
          if (availableMg.length === 0 && availableOg.length === 0) {
            return <MenuItem disabled>{t('All groups already attached')}</MenuItem>
          }
          const row = (key, name) => (
            <MenuItem key={key} onClick={() => toggleItemGroupChecked(key)} dense>
              <Checkbox
                edge="start"
                size="small"
                checked={itemGroupChecked.has(key)}
                tabIndex={-1}
                disableRipple
                onClick={(e) => { e.stopPropagation(); toggleItemGroupChecked(key) }}
              />
              <ListItemText primary={name} primaryTypographyProps={{ variant: 'body2' }} />
            </MenuItem>
          )
          return (
            <>
              {availableMg.length > 0 && (
                <Box sx={{ px: 2, pt: 0.5 }}>
                  <Typography variant="caption" sx={sectionLabelSx.mg}>
                    {t('Modifier Groups')}
                  </Typography>
                </Box>
              )}
              {availableMg.map((g) => row(`mg:${g._id}`, g.name))}
              {availableOg.length > 0 && (
                <Box sx={{ px: 2, pt: 0.5 }}>
                  <Typography variant="caption" sx={sectionLabelSx.og}>
                    {t('Option Groups')}
                  </Typography>
                </Box>
              )}
              {availableOg.map((g) => row(`og:${g._id}`, g.name))}
            </>
          )
        })()}
        <Divider />
        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size="small" onClick={closeItemGroupMenu} sx={{ textTransform: 'none' }}>
            {t('Cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={itemGroupBusy || itemGroupChecked.size === 0}
            onClick={applyAttachGroupsToItem}
            sx={{ textTransform: 'none', fontWeight: 'bold' }}
          >
            {itemGroupBusy ? <CircularProgress size={18} /> : t('Add {{n}}', { n: itemGroupChecked.size })}
          </Button>
        </Box>
      </MuiMenu>

      {/* Multi-size quick price editor — one field per size, saved on the way
          out of each field just like the single-price cell. */}
      <Popover
        open={Boolean(variantEdit)}
        anchorEl={variantEdit?.anchor}
        onClose={closeVariantEdit}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { ...menuPaperSx, p: 2, width: 260 } }}
      >
        <Typography variant="caption" fontWeight="bold" sx={{ color: mw.color.textMuted }}>
          {variantEdit ? t('Edit prices — {{name}}', { name: variantEdit.name }) : ''}
        </Typography>
        {variantEdit && Object.keys(variantEdit.values).length === 0 && (
          <Typography variant="body2" sx={{ color: mw.color.textFaint, mt: 1 }}>
            {t('No sizes yet — open the item to add sizes.')}
          </Typography>
        )}
        {variantEdit && Object.entries(variantEdit.values).map(([size, val]) => (
          <Box key={size} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Typography variant="body2" sx={{ flex: 1, color: mw.color.textPrimary, fontWeight: 600 }} noWrap>
              {size}
            </Typography>
            <TextField
              size="small"
              type="number"
              value={val}
              onChange={(e) => setVariantEdit(prev => ({ ...prev, values: { ...prev.values, [size]: e.target.value } }))}
              onBlur={() => commitVariantEdit()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitVariantEdit() }
                // Esc drops the untyped edit and leaves the saved price alone.
                else if (e.key === 'Escape') setVariantEdit(prev => (prev ? { ...prev, values: { ...prev.saved } } : prev))
              }}
              inputProps={{ step: '0.01', min: 0, style: { width: 64 } }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 2 }}>
          <Typography variant="caption" sx={{ color: mw.color.textFaint, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {priceSaving ? <CircularProgress size={12} /> : null}
            {priceSaving ? t('Saving...') : t('Saves automatically')}
          </Typography>
          <Button size="small" onClick={closeVariantEdit} sx={{ textTransform: 'none', fontWeight: 'bold' }}>
            {t('Done')}
          </Button>
        </Box>
      </Popover>

      {/* Saved toast — floats over the page after the editor closes */}
      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setToast('')} sx={{ boxShadow: mw.shadow.pop, borderRadius: mw.radius.sm }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default ItemsTab
